import React, { useCallback, useContext, useEffect, useState } from "react";
import { useFormik } from "formik";
import { Button, Col, Form, Row } from "react-bootstrap";
import { toast } from "react-toastify";
import * as Yup from "yup";
import ButtonSubmitFormikPermission from "../../../components/UI/Button/ButtonSubmitFormikPermission";
import FormAlert from "../../../components/UI/FormAlert/FormAlert";
import OverlayInfoAdicional from "../../../components/UI/Overlays/OverlayInfoAdicional";
import SelectInput from "../../../components/UI/SelectInput/SelectInput";
import TextInputFormik from "../../../components/UI/TextInput/TextInputFormik";
import TextSpinner from "../../../components/UI/Spinner/TextSpinner";
import { AuthContext } from "../../../context/AuthContext";
import funcionalidades from "../../../context/AuthContext.funcionalidades";
import useApiClient, { apiRoutes } from "../../../hooks/apiClient";

const validationSchema = Yup.object().shape({
  sqLivroFiscal: Yup.string().default(""),
  nomeLivroFiscal: Yup.string()
    .ensure()
    .default("")
    .required("Informe o livro")
    .min(1, "Nome muito curto")
    .max(500, "Nome muito longo"),
  tipoCategoria: Yup.string().default("").required("Informe a categoria"),
});

const initialValues = {};
validationSchema._nodes.forEach((key) => {
  initialValues[key] = "";
});

const FormLivroFiscal = ({ history, location }) => {
  const { state } = location;
  const { isAllowed } = useContext(AuthContext);
  const [dadosInfo, setDadosInfo] = useState({});

  // Formik
  const { request: requestForm, data: dataForm, errors: errorForm, isLoading } = useApiClient();

  const handleSubmit = useCallback(
    (values) => {
      if (values.sqLivroFiscal) requestForm(apiRoutes.fiscalizacao.parametrizacao.alteraLivroFiscal, values);
      else requestForm(apiRoutes.fiscalizacao.parametrizacao.incluiLivroFiscal, values);
    },
    [requestForm],
  );

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: handleSubmit,
  });

  const { setSubmitting, setErrors, isSubmitting, isValid } = formik;

  useEffect(() => {
    if ((dataForm || errorForm) && isSubmitting && !isLoading) formik.setSubmitting(false);

    if (errorForm && isValid && !isLoading) formik.setErrors({ ...formik.errors, ...errorForm });
  }, [formik, setSubmitting, setErrors, isSubmitting, isValid, dataForm, errorForm, isLoading]);

  useEffect(() => {
    if (!dataForm && !errorForm) return;

    if (dataForm) {
      toast.success("Livro Fiscal salvo com sucesso");
      history.push("/app/livroFiscal");
    }
  }, [dataForm, errorForm, history]);

  // Carregar item
  const {
    request: livroFiscalRequest,
    data: livroFiscalData,
    errors: livroFiscalErrors,
    isLoading: livroFiscalIsLoading,
  } = useApiClient();

  useEffect(() => {
    if (state && state.itemKey) {
      livroFiscalRequest(apiRoutes.fiscalizacao.parametrizacao.consultaLivroFiscal, { id: state.itemKey });
      delete state.itemKey;
    }
  }, [livroFiscalRequest, state]);

  useEffect(() => {
    if ((!livroFiscalData && !livroFiscalErrors) || livroFiscalIsLoading) return;

    if (!formik.values.sqLivroFiscal && livroFiscalData && livroFiscalData.result.length) {
      const info = {
        codigo: livroFiscalData?.result[0]?.sqLivroFiscal,
        data: {
          documentoInclusao: livroFiscalData?.result[0]?.usuarioInclusao,
          nomeFormalInclusao: livroFiscalData?.result[0]?.nomeFormalInclusao,
          nomeFormalAlteracao: livroFiscalData?.result[0]?.nomeFormalAlteracao,
          documentoAlteracao: livroFiscalData?.result[0]?.usuarioAlteracao,
          dataInclusao: livroFiscalData?.result[0]?.dataAlteracao,
          dataAlteracao: livroFiscalData?.result[0]?.dataInclusao,
        },
      };

      formik.setValues({ ...livroFiscalData.result[0] });
      setDadosInfo(info);
    }

    if (livroFiscalErrors) {
      toast.error(livroFiscalErrors.message);
    }
  }, [formik, livroFiscalData, livroFiscalErrors, livroFiscalIsLoading]);

  return (
    <>
      <h1 className="h3 mb-4 text-gray-800">Livro Fiscal</h1>
      <div className="row">
        <div className="col-lg-12">
          <div className="card shadow mb-4">
            <div className="card-header py-3">
              <Row>
                <Col sm="11">
                  <h6 className="m-0 font-weight-bold text-primary flex-grow-1">
                    {formik.values.sqLivroFiscal ? "Alteração do Livro Fiscal" : "Inclusão do Livro Fiscal"}
                  </h6>
                </Col>
                <Col sm="1" className="text-right">
                  <OverlayInfoAdicional data={dadosInfo} keyFieldName="sqLivroFiscal" />
                </Col>
              </Row>
            </div>
            <div className="card-body">
              <FormAlert
                formik={formik}
                data={dataForm}
                errors={errorForm}
                isLoading={isLoading || livroFiscalIsLoading}
              />
              <Form noValidate onSubmit={formik.handleSubmit}>
                <fieldset
                  disabled={
                    formik.values.sqLivroFiscal
                      ? !isAllowed(funcionalidades.livroFiscal.alterar)
                      : !isAllowed(funcionalidades.livroFiscal.inserir)
                  }
                >
                  <Row>
                    <Form.Group as={Col} sm={12}>
                      <Form.Label>Livro</Form.Label>
                      <TextInputFormik formik={formik} field="nomeLivroFiscal" maxLength="100" />
                    </Form.Group>
                    <Form.Group as={Col} sm={12} md={3}>
                      <Form.Label>Categoria</Form.Label>
                      <SelectInput
                        formik={formik}
                        field="tipoCategoria"
                        options={[
                          { key: "0", value: "", text: "Selecione" },
                          { value: "LF", text: "Livros Fiscais" },
                          { value: "NF", text: "Notas Fiscais e/ou equivalentes" },
                          { value: "DI", text: "Outros Documentos de Interesse do Fisco" },
                          { value: "AM", text: "Arquivos Magneticos" },
                        ]}
                      />
                    </Form.Group>
                  </Row>
                </fieldset>
                <Row className="mt-2">
                  <Col>
                    {formik.isSubmitting ? (
                      <TextSpinner text="Registrando Livro Fiscal..." />
                    ) : (
                      <ButtonSubmitFormikPermission
                        permission={
                          formik.values.sqLivroFiscal
                            ? funcionalidades.livroFiscal.alterar
                            : funcionalidades.livroFiscal.inserir
                        }
                        disabled={formik.isSubmitting}
                        name="Salvar"
                      />
                    )}
                    {"    "}
                    {!formik.isSubmitting && !formik.values.sqLivroFiscal && (
                      <Button variant="light" className="mr-3" onClick={formik.handleReset}>
                        Limpar
                      </Button>
                    )}
                    {"    "}
                    {!formik.isSubmitting && (
                      <Button variant="danger" onClick={() => history.goBack()}>
                        Cancelar
                      </Button>
                    )}
                  </Col>
                </Row>
              </Form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default FormLivroFiscal;
